export interface AppBundleSummary {
  id: string;
  projectId: string;
  createdAt: string;
  name?: string;
  description?: string;
  workItemIds: string[];
  workItems: Array<{
    id: string;
    title: string;
    status: string;
    owner?: string | null;
    templateId?: string | null;
    linkedRequirementIds?: string[] | null;
  }>;
}

export interface AppBundleManifestEntry {
  path: string;
  sha256: string;
  bytes: number;
}

export interface AppBundleManifest {
  createdAt: string;
  bundleId: string;
  fileCount: number;
  files: AppBundleManifestEntry[];
}

export interface AppBundleResponse {
  bundleId: string;
  bundle: AppBundleSummary;
  manifest: AppBundleManifest;
  bundleUrl: string;
  manifestUrl: string;
  archiveUrl: string;
}

interface CreateResponse {
  ok: boolean;
  error?: string;
  bundleId?: string;
  bundle?: AppBundleSummary;
  manifest?: AppBundleManifest;
  bundleUrl?: string;
  manifestUrl?: string;
  archiveUrl?: string;
}

function handleError(response: Response, payload: { error?: string }, fallback: string): never {
  const message = payload?.error ?? `${fallback} (status ${response.status})`;
  throw new Error(message);
}

export async function createAppBundle(
  projectId: string,
  input: { name?: string; description?: string; workItemIds: string[] },
): Promise<AppBundleResponse> {
  const response = await fetch(`/api/projects/${projectId}/app-bundles`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  const payload = (await response.json()) as CreateResponse;

  if (!response.ok || payload.ok === false || !payload.bundleId || !payload.bundle || !payload.manifest) {
    handleError(response, payload, "Failed to generate app bundle");
  }

  return {
    bundleId: payload.bundleId,
    bundle: payload.bundle,
    manifest: payload.manifest,
    bundleUrl: payload.bundleUrl ?? "",
    manifestUrl: payload.manifestUrl ?? "",
    archiveUrl: payload.archiveUrl ?? "",
  };
}
